"use client";

import { useRef, useState, useCallback } from "react";
import { Camera, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoThumbnail } from "@/components/molecules/photo-thumbnail";
import { ImageEditor } from "@/components/organisms/image-editor";
import { usePhotos } from "@/core/hooks/usePhotos";
import { compressImage } from "@/core/utils/image";

interface PhotoSectionProps {
  /** ID de la mesure (pour lier les photos) */
  mesureId: string;
  /** Numéro de la mesure (pour le nommage auto des fichiers) */
  mesureNumero: number;
}

/** Taille max brute acceptée (20 Mo — les téléphones modernes produisent des photos lourdes) */
const MAX_RAW_SIZE = 20 * 1024 * 1024;

/**
 * PhotoSection — Section photos d'une fiche de mesure.
 * Capture caméra + import galerie, compression auto, carousel, crop.
 */
export function PhotoSection({ mesureId, mesureNumero }: PhotoSectionProps) {
  const { photos, addPhoto, deletePhoto, updatePhotoBlob } = usePhotos(mesureId);

  // Refs pour les inputs file cachés
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // État éditeur crop
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);
  const [editingBlob, setEditingBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Compteur ref pour le nommage (évite de recréer le callback à chaque photo ajoutée)
  const photoCountRef = useRef(photos.length);
  photoCountRef.current = photos.length;

  /** Traite un fichier sélectionné (caméra ou galerie) */
  const handleFile = useCallback(
    async (file: File) => {
      setError(null);

      // Validation type
      if (!file.type.startsWith("image/")) {
        setError("Format non supporté. Utilisez une image (JPEG, PNG...).");
        return;
      }

      // Validation taille brute
      if (file.size > MAX_RAW_SIZE) {
        setError("Image trop volumineuse (max 20 Mo).");
        return;
      }

      try {
        // Compression automatique
        const compressed = await compressImage(file);

        // Nommage auto : M{numero}-P{index+1}.jpg
        const index = photoCountRef.current + 1;
        const fileName = `M${mesureNumero}-P${index}.jpg`;

        await addPhoto(compressed, fileName);
      } catch {
        setError("Impossible de traiter cette image. Réessayez.");
      }
    },
    [mesureNumero, addPhoto]
  );

  /** Handler onChange des inputs file */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset l'input pour permettre de reprendre la même photo
      e.target.value = "";
    },
    [handleFile]
  );

  /** Ouvre l'éditeur crop pour une photo */
  const openEditor = useCallback(
    (photoId: string, blob: Blob) => {
      setEditingPhotoId(photoId);
      setEditingBlob(blob);
    },
    []
  );

  /** Sauvegarde après crop */
  const handleEditorSave = useCallback(
    async (editedBlob: Blob) => {
      if (editingPhotoId) {
        await updatePhotoBlob(editingPhotoId, editedBlob);
      }
      setEditingPhotoId(null);
      setEditingBlob(null);
    },
    [editingPhotoId, updatePhotoBlob]
  );

  /** Annule le crop */
  const handleEditorCancel = useCallback(() => {
    setEditingPhotoId(null);
    setEditingBlob(null);
  }, []);

  // Mode éditeur : affiche l'ImageEditor au lieu du carousel
  if (editingBlob) {
    return (
      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
          Recadrer la photo
        </h3>
        <ImageEditor
          imageBlob={editingBlob}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
        Photos
      </h3>

      {/* Boutons capture */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => cameraInputRef.current?.click()}
        >
          <Camera className="size-4" />
          Photo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => galleryInputRef.current?.click()}
        >
          <ImagePlus className="size-4" />
          Galerie
        </Button>
      </div>

      {/* Inputs file cachés */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleInputChange}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Message d'erreur inline */}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Carousel photos */}
      {photos.length > 0 ? (
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none">
          {photos.map((photo) => (
            <PhotoThumbnail
              key={photo.id}
              blob={photo.blob}
              fileName={photo.fileName}
              onDelete={() => deletePhoto(photo.id)}
              onTap={() => openEditor(photo.id, photo.blob)}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Aucune photo
        </p>
      )}
    </section>
  );
}
